import { useState, useRef, useCallback, useEffect } from 'react';
import { useTheme } from '../hooks/useTheme';
import { AudioCapture } from '../lib/audioCapture';
import { classifyVoiceType, getSingerComparisons, rangeDescription } from '../lib/vocalRange';
import { frequencyToNote } from '../lib/noteMap';
import { useVeloraStore } from '../store/useVeloraStore';
import type { VoiceType } from '@shared/protocol';

type Phase = 'intro' | 'low' | 'high' | 'result';

const PHASE_DURATION = 12000;
const MIN_VALID_FREQ = 60;
const MAX_VALID_FREQ = 1400;
const SILENCE_THRESHOLD = 0.02;

const VOICE_COLORS: Record<VoiceType | 'Unknown', string> = {
  Bass: '#60a5fa', Baritone: '#818cf8', Tenor: '#a78bfa',
  Alto: '#f472b6', 'Mezzo-Soprano': '#e879f9', Soprano: '#f9a8d4', Unknown: '#AFA9EC',
};

const VOICE_TYPES_SCALE: Array<{ type: VoiceType; label: string }> = [
  { type: 'Soprano',       label: 'Soprano' },
  { type: 'Mezzo-Soprano', label: 'Mezzo' },
  { type: 'Alto',          label: 'Alto' },
  { type: 'Tenor',         label: 'Tenor' },
  { type: 'Baritone',      label: 'Baritone' },
  { type: 'Bass',          label: 'Bass' },
];

export default function VocalRange() {
  const theme = useTheme();
  const [phase, setPhase] = useState<Phase>('intro');
  const [lowestHz, setLowestHz] = useState(Infinity);
  const [highestHz, setHighestHz] = useState(0);
  const [currentHz, setCurrentHz] = useState(0);
  const [timeLeft, setTimeLeft] = useState(PHASE_DURATION / 1000);
  const capture = useRef<AudioCapture | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  const countdown = useRef<ReturnType<typeof setInterval> | null>(null);
  const { setVocalRange, voiceType: savedVoiceType } = useVeloraStore();

  const cleanup = useCallback(() => {
    capture.current?.stop(); capture.current = null;
    if (timer.current) { clearInterval(timer.current); timer.current = null; }
    if (countdown.current) { clearInterval(countdown.current); countdown.current = null; }
  }, []);

  useEffect(() => () => cleanup(), [cleanup]);

  const startPhase = useCallback(async (p: Phase) => {
    cleanup();
    setPhase(p);
    setTimeLeft(PHASE_DURATION / 1000);
    if (p === 'low') { setLowestHz(Infinity); setHighestHz(0); }
    capture.current = new AudioCapture(({ frequency, rms }) => {
      if (rms < SILENCE_THRESHOLD || frequency <= 0) return;
      const f = frequency;
      if (f < MIN_VALID_FREQ || f > MAX_VALID_FREQ) return;
      setCurrentHz(f);
      if (p === 'low')  setLowestHz(prev => Math.min(prev, f));
      if (p === 'high') setHighestHz(prev => Math.max(prev, f));
    });
    await capture.current.start();
    countdown.current = setInterval(() => setTimeLeft(t => Math.max(0, t - 1)), 1000);
    timer.current = setTimeout(() => {
      cleanup();
      if (p === 'low') startPhase('high');
      if (p === 'high') finalize();
    }, PHASE_DURATION);
  }, [cleanup]);

  const finalize = useCallback(() => { setPhase('result'); setCurrentHz(0); }, []);

  const saveResult = useCallback(() => {
    if (lowestHz === Infinity || highestHz === 0) return;
    const type = classifyVoiceType(lowestHz, highestHz);
    const loN = frequencyToNote(lowestHz).fullName;
    const hiN = frequencyToNote(highestHz).fullName;
    setVocalRange(lowestHz, highestHz, loN, hiN, type);
  }, [lowestHz, highestHz, setVocalRange]);

  useEffect(() => {
    if (phase === 'result' && lowestHz !== Infinity && highestHz > 0) saveResult();
  }, [phase]);

  const voiceType = lowestHz !== Infinity && highestHz > 0
    ? classifyVoiceType(lowestHz, highestHz)
    : (savedVoiceType ?? 'Unknown');
  const singers = getSingerComparisons(voiceType as VoiceType);
  const color = VOICE_COLORS[voiceType as VoiceType] ?? '#AFA9EC';

  const TimerRing = ({ seconds }: { seconds: number }) => {
    const pct = (seconds / (PHASE_DURATION / 1000)) * 100;
    return (
      <svg width="100" height="100" viewBox="0 0 36 36" className="mx-auto mb-3">
        <circle cx="18" cy="18" r="15.9" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="2.8"/>
        <circle cx="18" cy="18" r="15.9" fill="none" stroke={theme.accent} strokeWidth="2.8"
          strokeDasharray={`${pct} 100`} strokeDashoffset="25" strokeLinecap="round"
          style={{ transition: 'stroke-dasharray 1s linear', filter: `drop-shadow(0 0 4px ${theme.accent}70)` }}/>
        <text x="18" y="22" textAnchor="middle" fontSize="10" fill={theme.text} fontFamily="JetBrains Mono, monospace">
          {seconds}s
        </text>
      </svg>
    );
  };

  return (
    <div className="flex flex-col min-h-screen pb-24" style={{ background: 'transparent' }}>
      {/* Header */}
      <div className="safe-top px-5 pt-4 pb-5 fade-up">
        <p className="text-xs tracking-widest uppercase font-medium mb-1"
          style={{ color: theme.dim, fontFamily: 'Syne, sans-serif' }}>Test</p>
        <h1 className="text-2xl font-bold" style={{ color: theme.text, fontFamily: 'Syne, sans-serif' }}>
          VOCAL RANGE
        </h1>
      </div>

      <div className="flex-1 px-4">

        {/* INTRO */}
        {phase === 'intro' && (
          <div className="flex flex-col items-center gap-4 fade-up">
            <div className="text-6xl">🎤</div>
            <div className="glass-card rounded-2xl p-5 text-center w-full">
              <h2 className="text-base font-semibold mb-2" style={{ color: theme.text, fontFamily: 'Syne, sans-serif' }}>
                How it works
              </h2>
              <p className="text-sm leading-relaxed" style={{ color: theme.muted }}>
                Sing your <strong style={{ color: theme.accent }}>lowest</strong> note for 12 seconds,
                then your <strong style={{ color: theme.accent }}>highest</strong> note for 12 seconds.
                We'll detect your voice type automatically.
              </p>
            </div>
            {savedVoiceType && (
              <div className="glass-card rounded-xl p-3.5 w-full"
                style={{ background: `${VOICE_COLORS[savedVoiceType as VoiceType] ?? '#7F77DD'}10`,
                  borderColor: `${VOICE_COLORS[savedVoiceType as VoiceType] ?? '#7F77DD'}28` }}>
                <p className="text-xs tracking-widest uppercase mb-1"
                  style={{ color: theme.dim, fontFamily: 'Syne, sans-serif' }}>Last result</p>
                <p className="font-bold text-lg" style={{
                  color: VOICE_COLORS[savedVoiceType as VoiceType] ?? '#7F77DD',
                  fontFamily: 'Syne, sans-serif',
                }}>{savedVoiceType}</p>
              </div>
            )}
            <button onClick={() => startPhase('low')}
              className="w-full py-4 rounded-2xl text-base font-semibold transition-all active:scale-95"
              style={{
                background: 'rgba(83,74,183,0.28)',
                backdropFilter: 'blur(24px)',
                WebkitBackdropFilter: 'blur(24px)',
                border: '1px solid rgba(127,119,221,0.40)',
                color: theme.text,
                fontFamily: 'Syne, sans-serif',
                boxShadow: '0 8px 28px rgba(83,74,183,0.3), inset 0 1px 0 rgba(255,255,255,0.12)',
              }}>
              Start Test
            </button>
          </div>
        )}

        {/* RECORDING PHASES */}
        {(phase === 'low' || phase === 'high') && (
          <div className="flex flex-col items-center gap-4 fade-up">
            <div className="glass-card rounded-2xl p-4 w-full text-center"
              style={{
                background: phase === 'low' ? 'rgba(96,165,250,0.08)' : 'rgba(249,168,212,0.08)',
                borderColor: phase === 'low' ? 'rgba(96,165,250,0.28)' : 'rgba(249,168,212,0.28)',
              }}>
              <p className="text-xs tracking-widest uppercase mb-1" style={{ color: theme.muted }}>
                {phase === 'low' ? 'Step 1 of 2' : 'Step 2 of 2'}
              </p>
              <h2 className="text-xl font-bold" style={{ color: theme.text, fontFamily: 'Syne, sans-serif' }}>
                {phase === 'low' ? 'Sing your LOWEST note' : 'Sing your HIGHEST note'}
              </h2>
              <p className="text-sm mt-1" style={{ color: theme.muted }}>
                {phase === 'low' ? 'Go as low as you comfortably can' : 'Go as high as you comfortably can'}
              </p>
            </div>

            <TimerRing seconds={timeLeft} />

            <div className="text-center">
              <div className="text-5xl font-bold mb-1"
                style={{ color: currentHz > 0 ? theme.text : theme.dim, fontFamily: 'Syne, sans-serif',
                  textShadow: currentHz > 0 ? `0 0 20px ${theme.accent}60` : 'none' }}>
                {currentHz > 0 ? frequencyToNote(currentHz).fullName : '•'}
              </div>
              {currentHz > 0 && (
                <div className="text-sm font-mono" style={{ color: theme.muted }}>
                  {currentHz.toFixed(1)} Hz
                </div>
              )}
            </div>

            <div className="w-full glass-card rounded-xl p-3.5">
              <div className="flex justify-between text-sm">
                <div>
                  <p className="text-xs mb-0.5" style={{ color: theme.dim }}>Lowest captured</p>
                  <p className="font-mono font-bold" style={{ color: '#60a5fa' }}>
                    {lowestHz !== Infinity ? `${frequencyToNote(lowestHz).fullName} (${lowestHz.toFixed(0)} Hz)` : '--'}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs mb-0.5" style={{ color: theme.dim }}>Highest captured</p>
                  <p className="font-mono font-bold" style={{ color: '#f9a8d4' }}>
                    {highestHz > 0 ? `${frequencyToNote(highestHz).fullName} (${highestHz.toFixed(0)} Hz)` : '--'}
                  </p>
                </div>
              </div>
            </div>

            <button onClick={() => { cleanup(); if (phase === 'low') startPhase('high'); else finalize(); }}
              className="w-full py-3 rounded-xl text-sm glass-card"
              style={{ color: theme.muted }}>
              Skip to {phase === 'low' ? 'High' : 'Results'}
            </button>
          </div>
        )}

        {/* RESULT */}
        {phase === 'result' && lowestHz !== Infinity && highestHz > 0 && (
          <div className="flex flex-col gap-4 fade-up">
            {/* Voice type hero */}
            <div className="glass-card rounded-3xl p-6 text-center relative overflow-hidden"
              style={{ background: `${color}10`, borderColor: `${color}30` }}>
              <div className="absolute inset-0 pointer-events-none"
                style={{ background: `radial-gradient(ellipse 80% 60% at 50% 0%, ${color}12, transparent)` }} />
              <p className="text-xs tracking-widest uppercase mb-2 relative"
                style={{ color: theme.muted, fontFamily: 'Syne, sans-serif' }}>Your Voice Type</p>
              <div className="text-5xl font-bold mb-2 relative"
                style={{ color, fontFamily: 'Syne, sans-serif', textShadow: `0 0 24px ${color}60` }}>
                {voiceType}
              </div>
              <p className="text-sm relative" style={{ color: theme.muted }}>
                {rangeDescription(lowestHz, highestHz)}
              </p>
            </div>

            {/* Range bar */}
            <div className="glass-card rounded-2xl p-4">
              <p className="text-xs tracking-widest uppercase mb-3"
                style={{ color: theme.dim, fontFamily: 'Syne, sans-serif' }}>Your Range</p>
              <div className="flex items-center gap-3">
                <div className="text-center">
                  <div className="text-base font-bold font-mono" style={{ color: '#60a5fa' }}>
                    {frequencyToNote(lowestHz).fullName}
                  </div>
                  <div className="text-xs" style={{ color: theme.muted }}>Low</div>
                </div>
                <div className="flex-1 h-2 rounded-full"
                  style={{ background: `linear-gradient(to right, #60a5fa, ${color}, #f9a8d4)`,
                    boxShadow: `0 0 8px ${color}50` }} />
                <div className="text-center">
                  <div className="text-base font-bold font-mono" style={{ color: '#f9a8d4' }}>
                    {frequencyToNote(highestHz).fullName}
                  </div>
                  <div className="text-xs" style={{ color: theme.muted }}>High</div>
                </div>
              </div>
            </div>

            {/* Voice scale */}
            <div className="glass-card rounded-2xl p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs tracking-widest uppercase"
                  style={{ color: theme.dim, fontFamily: 'Syne, sans-serif' }}>Voice Type Scale</p>
                <p className="text-xs" style={{ color: theme.dim }}>Highest ↑</p>
              </div>
              <div className="flex flex-col gap-1.5">
                {VOICE_TYPES_SCALE.map(({ type, label }) => {
                  const isActive = type === voiceType;
                  const typeColor = VOICE_COLORS[type];
                  return (
                    <div key={type} className="flex items-center gap-3">
                      <span className="text-xs w-16 text-right shrink-0 font-medium"
                        style={{ color: isActive ? typeColor : theme.dim, fontFamily: 'Syne, sans-serif' }}>
                        {label}
                      </span>
                      <div className="relative flex-1 h-5 rounded-lg overflow-hidden glass-card"
                        style={{ background: isActive ? `${typeColor}12` : undefined,
                          borderColor: isActive ? `${typeColor}35` : undefined }}>
                        {isActive && (
                          <>
                            <div className="absolute inset-0 rounded-lg"
                              style={{ background: `linear-gradient(90deg, ${typeColor}50, ${typeColor}20)` }} />
                            <div className="absolute inset-0 flex items-center px-2">
                              <span className="text-xs font-bold" style={{ color: typeColor }}>← You</span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-end mt-2">
                <p className="text-xs" style={{ color: theme.dim }}>Lowest ↓</p>
              </div>
            </div>

            {/* Famous singers */}
            {singers.length > 0 && (
              <div className="glass-card rounded-2xl p-4">
                <p className="text-xs tracking-widest uppercase mb-3"
                  style={{ color: theme.dim, fontFamily: 'Syne, sans-serif' }}>Similar Voice to</p>
                <div className="flex gap-2 flex-wrap">
                  {singers.map(s => (
                    <span key={s} className="px-3 py-1 rounded-full text-sm glass-pill"
                      style={{ color, borderColor: `${color}30` }}>
                      {s}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <button onClick={() => setPhase('intro')}
              className="w-full py-3 rounded-xl text-sm font-medium transition-all active:scale-95 glass-card"
              style={{ color: theme.text }}>
              Test Again
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
